import { getAvailableStorageList, storeFileToServer } from '@api/services/box-storage.service';
import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import RequiredMark from '@components/FormFields/RequiredMark';
import { useUploadNavigationGuard } from '@hooks/useUploadNavigationGuard';
import { Button, Card, CircularProgress, HelpText, Label } from '@hyperflake/react-ui-library';
import { InfoCircleIcon, UploadIcon } from '@icon/index';
import { FormHelperText } from '@library/forms';
import SingleCategoryInput from '@library/forms/components/SingleInputSelector/SingleInputSelector';
import { useQuery } from '@tanstack/react-query';
import { getResellerIdFromRedux } from '@utils/common';
import { getFileExtension } from '@utils/file-utils';
import axios from 'axios';
import { ErrorMessage, Form, Formik } from 'formik';
import http from 'httpConfig/http';
import useCategoryListData from 'modules/box-strorage/hooks/useCategoryListData';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

// ✅ use your configured http client (same style as getSignedUrl)

/* -------------------------------------------------------------------------------------------------
 * Multipart-only config (STATIC chunk size)
 * -----------------------------------------------------------------------------------------------*/

const MB = 1024 * 1024;
const PART_SIZE_BYTES = 64 * MB; // fixed chunk size
const MAX_PARTS = 10000;

function splitIntoStaticParts(file: File) {
    const totalParts = Math.ceil(file.size / PART_SIZE_BYTES);

    if (totalParts > MAX_PARTS) {
        throw new Error(`File too large: would create ${totalParts} parts (> ${MAX_PARTS}). Increase PART_SIZE_BYTES.`);
    }

    const parts = Array.from({ length: totalParts }, (_, i) => {
        const start = i * PART_SIZE_BYTES;
        const end = Math.min(start + PART_SIZE_BYTES, file.size);
        return { partNumber: i + 1, start, end };
    });

    return { parts, totalParts, partSize: PART_SIZE_BYTES };
}

/* -------------------------------------------------------------------------------------------------
 * Validation Schema
 * -----------------------------------------------------------------------------------------------*/

const validationSchema = Yup.object().shape({
    storage: Yup.string().required('Storage is required'),
    fileName: Yup.string().required('File name is required'),
    category: Yup.string(),
    files: Yup.array()
        .of(Yup.mixed().required())
        .max(1, 'Only one file is allowed')
        .test('fileRequired', 'File is required', (files) => files?.length === 1),
});

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const DocumentUploadForm = () => {
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);

    const { category, isLoadingCategory } = useCategoryListData();

    // Upload concurrency (PUTs)
    const MAX_CONCURRENCY = 4;

    // Presign in batches (calling single-part endpoint multiple times)
    const PRESIGN_BATCH_SIZE = 20;

    // progress tracking per part (accurate even with concurrency)
    const uploadedByPartRef = useRef<Record<number, number>>({});

    const { data: storages, isLoading: storageIsLoading } = useQuery<string[]>(['storage-names'], async () =>
        getAvailableStorageList()
    );

    // ✅ API calls using your shared http client (interceptors handle auth)
    async function createMultipartUpload(payload: {
        filename: string;
        storage: string;
        contentType: string;
        extension?: string;
    }) {
        const { data } = await http.post(
            `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/box-storage/multipart/init`,
            payload
        );
        return data as { uploadId: string; key: string };
    }

    async function getMultipartUploadPartUrl(payload: {
        storage: string;
        key: string;
        uploadId: string;
        PartNumber: number;
        contentType?: string;
    }) {
        const { data } = await http.post(
            `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/box-storage/multipart/part-url`,
            payload
        );
        return data as { url: string };
    }

    async function completeMultipartUpload(payload: {
        storage: string;
        key: string;
        uploadId: string;
        fileName: string;
        parts: { PartNumber: number; ETag: string }[];
        contentType?: string;
        category?: string;
    }) {
        const { data } = await http.post(
            `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/box-storage/multipart/complete`,
            payload
        );
        return data;
    }

    const storageOption = useMemo(() => {
        if (!storages) return [];
        return [{ label: 'Select Storage', value: '' }, ...storages.map((val) => ({ label: val, value: val }))];
    }, [storages]);

    const initialValues = {
        storage: '',
        fileName: '',
        category: '',
        files: [] as File[],
    };

    const handleSubmit = async (values: typeof initialValues) => {
        try {
            const file = values.files?.[0];
            if (!file) return;

            const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
            const contentType = file.type || 'application/octet-stream';

            setUploadProgress(0);
            uploadedByPartRef.current = {};

            // A) init multipart (ALWAYS)
            const { uploadId, key } = await createMultipartUpload({
                filename: values.fileName,
                storage: values.storage,
                contentType,
                extension,
            });

            // B) split into static parts (ALWAYS)
            const { parts } = splitIntoStaticParts(file);

            // C) presign ON-DEMAND (fixes "Missing presigned url ... presign exhausted")
            const urlByPart = new Map<number, string>();
            const presignInFlight = new Map<number, Promise<string>>();

            const presignOne = async (partNumber: number, retries = 3): Promise<string> => {
                if (urlByPart.has(partNumber)) return urlByPart.get(partNumber)!;
                if (presignInFlight.has(partNumber)) return presignInFlight.get(partNumber)!;

                const task = (async () => {
                    let lastErr: any;

                    for (let i = 0; i < retries; i++) {
                        try {
                            // If you want throttling, wrap this call with your limiter:
                            // return presignLimiter.schedule(async () => { ... })
                            const { url } = await getMultipartUploadPartUrl({
                                storage: values.storage,
                                key,
                                uploadId,
                                PartNumber: partNumber,
                                contentType,
                            });

                            if (!url) throw new Error(`No url returned for part ${partNumber}`);

                            urlByPart.set(partNumber, url);
                            return url;
                        } catch (err) {
                            lastErr = err;
                            await new Promise((r) => setTimeout(r, 250 * (i + 1)));
                        }
                    }

                    throw lastErr;
                })();

                presignInFlight.set(partNumber, task);

                try {
                    return await task;
                } finally {
                    presignInFlight.delete(partNumber);
                }
            };

            // Optional warm-up (first batch)
            await Promise.all(parts.slice(0, PRESIGN_BATCH_SIZE).map((p) => presignOne(p.partNumber)));

            // D) upload with limited concurrency
            const queue = [...parts];
            const etags: { PartNumber: number; ETag: string }[] = [];

            const updateOverallProgress = () => {
                const uploadedBytes = Object.values(uploadedByPartRef.current).reduce((a, b) => a + b, 0);
                const total = file.size || 1;
                const percent = Math.min(99, Math.round((uploadedBytes * 100) / total));
                setUploadProgress(percent);
            };

            const uploadOne = async (p: { partNumber: number; start: number; end: number }) => {
                const url = await presignOne(p.partNumber);

                const blob = file.slice(p.start, p.end);

                // If you want throttling for PUTs, wrap axios.put with your uploadLimiter.schedule(...)
                const res = await axios.put(url, blob, {
                    headers: { 'Content-Type': contentType },
                    onUploadProgress: (evt) => {
                        uploadedByPartRef.current[p.partNumber] = evt.loaded ?? 0;
                        updateOverallProgress();
                    },
                });

                const eTag = res.headers?.etag;
                if (!eTag) throw new Error(`No ETag returned for part ${p.partNumber}`);

                // mark fully uploaded
                uploadedByPartRef.current[p.partNumber] = p.end - p.start;
                updateOverallProgress();

                etags.push({ PartNumber: p.partNumber, ETag: eTag });
            };

            const workers = Array.from({ length: MAX_CONCURRENCY }, async () => {
                while (queue.length) {
                    const p = queue.shift();
                    if (!p) break;
                    await uploadOne(p);
                }
            });

            await Promise.all(workers);

            // E) complete multipart (sorted)
            etags.sort((a, b) => a.PartNumber - b.PartNumber);

            await completeMultipartUpload({
                storage: values.storage,
                fileName: values.fileName,
                key,
                uploadId,
                parts: etags,
                contentType,
                category: values.category,
            });

            // F) store metadata
            // await storeFileToServer({
            //     filename: values.fileName,
            //     key,
            //     category: values.category,
            //     storage: values.storage,
            // });

            setUploadProgress(100);
            toast.success('Upload complete');
            setUploadProgress(0);
            navigate('/box-storage');
        } catch (err: any) {
            console.error(err);
            toast.error(err?.message || 'Upload failed');
            setUploadProgress(0);
        }
    };

    return (
        <>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <Card className="mt-4">
                            <Card.Header>
                                <h6>Documents</h6>
                            </Card.Header>
                            <Card.Body>
                                <div>
                                    <Label>
                                        Storage <RequiredMark />
                                    </Label>
                                    <div className="flex gap-2">
                                        <FormikSelect name="storage" options={storageOption} />
                                        {storageIsLoading && <CircularProgress size={14} />}
                                    </div>
                                    <FormHelperText variant="error">
                                        <ErrorMessage name="storage" />
                                    </FormHelperText>
                                </div>

                                <div
                                    className="relative flex justify-center cursor-pointer bg-primary/10 py-6 rounded-lg mt-4"
                                    onClick={() => inputRef.current?.click()}
                                >
                                    <>
                                        <Button type="button" variant="shaded" size="xs" className="cursor-pointer">
                                            <UploadIcon />
                                            Upload File
                                        </Button>

                                        <input
                                            ref={inputRef}
                                            type="file"
                                            accept="*/*"
                                            className="hidden"
                                            onClick={(e) => {
                                                e.currentTarget.value = '';
                                            }}
                                            onChange={(event) => {
                                                const selectedFiles = event.currentTarget.files;
                                                if (selectedFiles?.[0]) {
                                                    setFieldValue('files', [selectedFiles[0]]);
                                                    setFieldValue('fileName', selectedFiles[0].name);
                                                }
                                            }}
                                        />
                                    </>
                                </div>

                                <ul className="mt-6 list-disc pl-5">
                                    {values.files.map((file, idx) => (
                                        <li key={idx} className="flex items-center gap-2">
                                            <DocFileIcon className="w-7" extension={getFileExtension(file.name)} />
                                            {file.name}
                                            <Button
                                                size="xs"
                                                type="button"
                                                className="ml-4"
                                                onClick={() => {
                                                    setFieldValue('files', []);
                                                    setFieldValue('fileName', '');
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-4">
                                    <Label>File Name</Label>
                                    <FormikInput name="fileName" />
                                    <HelpText variant="error">
                                        <ErrorMessage name="fileName" />
                                    </HelpText>
                                </div>

                                <div className="mt-4 w-full">
                                    <Label>Category</Label>
                                    <div className="relative">
                                        <SingleCategoryInput
                                            id="category"
                                            categories={!isLoadingCategory ? category : []}
                                            disabled={isLoadingCategory}
                                            selectedCategory={values.category}
                                            onSelectedCategoryChange={(cat) => setFieldValue('category', cat)}
                                        />
                                        {isLoadingCategory && (
                                            <div className="absolute right-3 text-primary">
                                                <span className="flex">
                                                    <CircularProgress size={16} />
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <FormHelperText variant="error">
                                        <ErrorMessage name="category" />
                                    </FormHelperText>
                                </div>

                                {uploadProgress > 0 && (
                                    <>
                                        <div className="mt-2 text-sm text-primary">Uploading: {uploadProgress}%</div>
                                        <div className="mt-2 text-sm text-warning-500 flex items-center gap-2">
                                            <div>
                                                <InfoCircleIcon />
                                            </div>
                                            <div>
                                                Please note: Refreshing or closing this page will interrupt and cancel
                                                the ongoing upload process.
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>

                        <Button type="submit" className="mt-6" disabled={isSubmitting || values.files.length === 0}>
                            {isSubmitting ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Form>
                )}
            </Formik>

            {/* <Popover open={showPopover} setOpen={cancelLeave} ... /> */}
        </>
    );
};

export default DocumentUploadForm;
